import styled from "@emotion/styled";
import type { CSSProperties, ComponentProps } from "react";

import { Icon, SkiliticsIcon, ToggleButton, ToggleButtonGroup, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatStrings } from "@skilitics/localization";

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background-color: ${({ theme }) => theme.outlineSoft};
  flex: 0 0 auto;
`;

const StyledToggleButton = styled(ToggleButton)`
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius}px !important;
  height: 24px;
  width: 24px;
  font-size: 15px;
  padding: 0;
  color: ${({ theme }) => theme.generalTextIcon};
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const LayoutPickerContainer = styled.div`
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 22px;
`;

type Layout = CSSProperties["flexDirection"] | "stacked";
type Wrap = CSSProperties["flexWrap"];

type Props = {
  layout?: Layout;
  onLayoutChange: (layout: Layout) => void;
  wrap?: Wrap;
  onWrapChange: (wrap: Wrap) => void;
};

export const LayoutPicker = ({ layout = "row", onLayoutChange, wrap, onWrapChange }: Props) => {
  const formatLayoutMessage = useFormatMessage(layoutFormatStrings);
  const formatMessage = useFormatMessage(formatStrings);

  const handleChange: ComponentProps<typeof ToggleButtonGroup>["onChange"] = (_e, value) => {
    // ToggleButtonGroup `value` is null when you click an already selected button.
    if (value && value !== layout) {
      onLayoutChange(value as Layout);
    }
  };

  const handleWrapChange: ComponentProps<typeof ToggleButton>["onChange"] = (_e, value) => {
    if (value && value !== wrap) {
      onWrapChange(value as Wrap);
    }
  };

  return (
    <LayoutPickerContainer>
      <StyledToggleButtonGroup onChange={handleChange} value={layout} exclusive color="primary">
        <Tooltip title={formatLayoutMessage("row")} placement="top">
          <StyledToggleButton value="row">
            <SkiliticsIcon icon="flow-selection" />
          </StyledToggleButton>
        </Tooltip>
        <Tooltip title={formatLayoutMessage("column")} placement="top">
          <StyledToggleButton value="column">
            <SkiliticsIcon icon="flow-selection" rotation={90} />
          </StyledToggleButton>
        </Tooltip>
        <Tooltip title={formatLayoutMessage("row-reverse")} placement="top">
          <StyledToggleButton value="row-reverse">
            <SkiliticsIcon icon="flow-selection" rotation={180} />
          </StyledToggleButton>
        </Tooltip>
        <Tooltip title={formatLayoutMessage("column-reverse")} placement="top">
          <StyledToggleButton value="column-reverse">
            <SkiliticsIcon icon="flow-selection" rotation={270} />
          </StyledToggleButton>
        </Tooltip>
        <Tooltip title={formatLayoutMessage("stacked")} placement="top">
          <StyledToggleButton value="stacked">
            <Icon icon="layer-group" size={14} />
          </StyledToggleButton>
        </Tooltip>
      </StyledToggleButtonGroup>
      <Divider />
      <Tooltip title={formatMessage(wrap === "wrap" ? "wrap" : "nowrap")} placement="top">
        <StyledToggleButton
          selected={wrap === "wrap"}
          value={wrap === "wrap" ? "nowrap" : "wrap"}
          onChange={handleWrapChange}
          color="primary"
        >
          <SkiliticsIcon
            icon="flow-wrap"
            rotation={layout.includes("column") ? 270 : undefined}
            flip={layout === "column" ? "vertical" : layout === "row-reverse" ? "horizontal" : undefined}
          />
        </StyledToggleButton>
      </Tooltip>
    </LayoutPickerContainer>
  );
};

const formatStrings = {
  wrap: {
    en: "Wrap",
  },
  nowrap: {
    en: "No Wrap",
  },
};

const layoutFormatStrings: Record<
  Extract<Layout, "row" | "column" | "row-reverse" | "column-reverse" | "stacked">,
  FormatStrings
> = {
  row: {
    en: "Horizontal Layout",
  },
  column: {
    en: "Vertical Layout",
  },
  "row-reverse": {
    en: "Horizontal Reverse",
  },
  "column-reverse": {
    en: "Vertical Reverse",
  },
  stacked: {
    en: "Stacked",
  },
};
